<template>
  <div class="collection-card">
    <RouterLink
      :to="card.url"
      custom
    >
      <template #default="{ href }">
        <a
          :href="href"
          :class="{
            'collection-card__link': true,
            'collection-card__link--break-text': breakText,
          }"
          draggable="false"
        >
          <div
            class="collection-card__image"
            @click="$emit('clicked', card)"
          >
            <div class="collection-card__overlay" />
            <ImageLazyload
              :src="imageUrl"
              alt=""
              class="collection-card__image ds-rounded-md ds-w-full"
              draggable="false"
            />
            <h4
              :class="{
                'collection-card__title': true,
                'collection-card__title--md': textSize === 'md',
                'collection-card__title--lg': textSize === 'lg',
              }"
            >
              {{ card.title }}
            </h4>
            <p
              v-if="card.subtitle"
              :class="{
                'collection-card__subtitle': true,
                'collection-card__subtitle--md': textSize === 'md',
                'collection-card__subtitle--lg': textSize === 'lg',
              }"
            >
              {{ card.subtitle }}
            </p>
          </div>
        </a>
      </template>
    </RouterLink>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';
import { CollectionCard } from './types';
import { ratioRegex, sizes } from './constants';
import ImageLazyload from '@monolith/legacy/components/global/image-lazyload.vue';
import { captureRetailerCoreDiscoveryException } from '@client/monolith/core/plugins/sentry/helper';

type Size = (typeof sizes)[number];

defineEmits(['clicked']);

const props = defineProps({
  card: {
    type: Object as PropType<CollectionCard>,
    required: true,
  },
  ratio: {
    type: String,
    required: false,
    default: undefined,
    validator: (value: string) => Boolean(value === undefined || ratioRegex.exec(value)),
  },
  textSize: {
    type: String as PropType<Size>,
    required: false,
    default: 'lg',
    validator: (value: Size) => sizes.includes(value),
  },
  breakText: {
    type: Boolean,
    required: false,
  },
});

const ratio = computed(() => {
  let a = 1;
  let b = 1;
  if (props.ratio) {
    [a, b] = ratioRegex.exec(props.ratio).map(Number).slice(1);
  }
  return { a, b };
});

const ratioA = computed(() => ratio.value.a);
const ratioB = computed(() => ratio.value.b);

const imageUrl = computed(() => {
  try {
    const url = new URL(props.card.image);
    url.searchParams.set('force_format', 'webp');
    url.searchParams.set('dpr', `${globalThis.devicePixelRatio}`);
    if (props.ratio) {
      url.searchParams.set('fit', 'crop');
      url.searchParams.set('crop', 'edges');
      url.searchParams.set('w', '300');
      url.searchParams.set('ar', `${ratioA.value / ratioB.value}`);
    }
    return url.href;
  } catch (error) {
    captureRetailerCoreDiscoveryException(error);
    return props.card.image; // Fallback to original URL
  }
});
</script>

<style scoped lang="scss">
@use '@css/abstracts/design-tokens' as d;

.collection-card {
  $root: &;
  cursor: pointer;

  &:hover {
    #{$root}__image {
      opacity: 0.88;
    }
    #{$root}__overlay {
      backdrop-filter: contrast(1.1) saturate(1.015);
    }
  }

  &__link {
    word-break: normal;
    &--break-text {
      word-break: break-all;
    }
  }

  &__image {
    position: relative;
    opacity: 1;
    z-index: 1;
    transition: opacity 120ms ease-in-out;
  }

  &__overlay {
    position: absolute;
    width: 100%;
    backdrop-filter: contrast(1) saturate(1);
    background-color: transparent;
    aspect-ratio: 1;
    transition: backdrop-filter 90ms ease-in-out;
    z-index: 2;
    height: 100%;
  }

  &__title {
    margin-top: 0.5rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--md {
      @include d.text('base');
    }
    &--lg {
      @include d.text('lg');
    }
  }

  &__subtitle {
    color: theme('colors.neutral.700');
    &--md {
      @include d.text('xs');
    }
    &--lg {
      @include d.text('sm');
    }
  }
}
</style>
